import { ReactNode } from "react"
import { FieldStatus, fieldBox } from "lib/components/Form/fieldStyles"

export interface InputShellProps {
  status?: FieldStatus
  disabled?: boolean
  height?: "fixed" | "auto" | "none"
  border?: "full" | "bottom" | "none"
  radius?: "none" | "sm" | "default" | "md" | "lg" | "xl"
  /** `start` keeps addons pinned to the top edge while a textarea grows underneath them. */
  align?: "center" | "start"
  /** Prefix such as `Rp` or `+62`. Style it with `fieldAddon()`. */
  leftAddon?: ReactNode
  rightAddon?: ReactNode
  /** Inline icon that sits inside the box, flush against the control. */
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  className?: string
  children: ReactNode
}

/**
 * Renders the bordered 36px row that encloses addons and icons alongside the control.
 *
 * Carries the border, hover and focus ring itself so the ring wraps the addons too; the control
 * inside must use `fieldControl({ inShell: true })`. Plain `Input` does not use it: its border stays
 * on the `<input>` so call-site `customClass` overrides still reach it.
 */
function InputShell({
  status = "default",
  disabled = false,
  height = "fixed",
  border = "full",
  radius = "md",
  align = "center",
  leftAddon,
  rightAddon,
  leadingIcon,
  trailingIcon,
  className = "",
  children,
}: InputShellProps) {
  return (
    <div
      className={`relative flex w-full ${align === "start" ? "items-start" : "items-center"} ${fieldBox({
        status,
        disabled,
        height,
        border,
        radius,
        focusMode: "within",
      })} ${className}`}>
      {leftAddon}
      {leadingIcon && <span className="flex shrink-0 items-center pl-2">{leadingIcon}</span>}
      {children}
      {trailingIcon && <span className="flex shrink-0 items-center pr-2">{trailingIcon}</span>}
      {rightAddon}
    </div>
  )
}

export default InputShell
